import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { orderService } from '../services/api';

const API_BASE_URL = 'https://api.iraq-parts.com';

const IRAQI_CITIES = [
  'بغداد',
  'البصرة',
  'الموصل',
  'أربيل',
  'النجف',
  'كربلاء',
  'كركوك',
  'السليمانية',
  'دهوك',
  'الرمادي',
  'الفلوجة',
  'تكريت',
  'سامراء',
  'بعقوبة',
  'الحلة',
  'الكوت',
  'الناصرية',
  'العمارة',
  'الديوانية',
  'السماوة'
];

const CheckoutPage = () => {
  const { cartItems, getTotalPrice, clearCart, updateQuantity } = useCart();
  const { isAuthenticated, user } = useAuth();
  const { success, error } = useNotification();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [deliveryCost, setDeliveryCost] = useState(5000);
  const [orderConfirmation, setOrderConfirmation] = useState(null);
  const [customerDetails, setCustomerDetails] = useState({
    name: user?.full_name || '',
    phone:  user?.phone || '',
    city: user?.city || '',
    address: user?.address || '',
    notes: ''
  });

  // Calculate delivery cost based on weight
  useEffect(() => {
    const totalWeight = cartItems.reduce((sum, item) => {
      const weight = parseFloat(item.weight) || 1; // Default 1kg if no weight
      return sum + (weight * item.quantity);
    }, 0);

    let cost = 5000; // Minimum delivery cost
    if (totalWeight > 5) {
      const additionalWeight = Math.ceil(totalWeight - 5);
      cost += additionalWeight * 1000;
    }
    setDeliveryCost(cost);
  }, [cartItems]);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return 'https://via.placeholder.com/80x80?text=No+Image';
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${API_BASE_URL}${imageUrl}`;
  };

  // eslint-disable-next-line no-unused-vars
  const totalWeight = cartItems.reduce((sum, item) => {
    const weight = parseFloat(item.weight) || 1;
    return sum + (weight * item.quantity);
  }, 0);

  const subtotal = getTotalPrice() || 0;
  const total = subtotal + deliveryCost;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNext = () => {
    if (currentStep === 2) {
      // Validate customer details
      if (! customerDetails.name || !customerDetails.phone || !customerDetails.city || !customerDetails.address) {
        error('الرجاء ملء جميع الحقول المطلوبة');
        return;
      }
    }
    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handlePlaceOrder = async () => {
    try {
      // Prepare order data
      const orderData = {
        total_amount: total,
        shipping_address: JSON.stringify({
          name: customerDetails.name,
          phone: customerDetails.phone,
          city: customerDetails.city,
          address: customerDetails.address,
          notes: customerDetails.notes
        }),
        payment_method: 'cash_on_delivery',
        items: cartItems.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
          price: parseFloat(item.price) || 0
        }))
      };

      // Send order to backend (guest or authenticated)
      let response;
      if (isAuthenticated) {
        response = await orderService.create(orderData);
      } else {
        response = await orderService.createGuest(orderData);
      }
      
      // Extract order ID from response (handle different response structures)
      const orderData_response = response.data?.order || response.data;
      const orderId = orderData_response?.id || Math.floor(Math.random() * 1000000);
      
      // Store order confirmation details BEFORE clearing cart
      const orderDate = new Date();
      const confirmationData = {
        orderId: orderId,
        orderNumber: `ORD-${String(orderId).padStart(6, '0')}`,
        items: [...cartItems], // Create a copy of cart items before clearing
        subtotal: subtotal,
        deliveryCost: deliveryCost,
        total: total,
        customerDetails: {...customerDetails}, // Create a copy
        createdAt: orderDate.toLocaleString('ar-IQ', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }),
        createdAtISO: orderDate.toISOString()
      };
      
      setOrderConfirmation(confirmationData);
      setCurrentStep(5); // Move to confirmation step FIRST
      
      // Clear cart after setting confirmation
      setTimeout(() => {
        clearCart();
      }, 100);
      
      success('تم تقديم الطلب بنجاح!');
    } catch (err) {
      console.error('Error placing order:', err);
      error('فشل تقديم الطلب.  يرجى المحاولة مرة أخرى.');
    }
  };

  if (cartItems.length === 0 && currentStep < 5) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-4 sm:py-8" dir="rtl">
      <h1 className="text-2xl sm:text-4xl font-bold mb-4 sm:mb-8">إتمام الطلب</h1>

      {/* Progress Steps */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center justify-between max-w-3xl mx-auto">
          {[1, 2, 3, 4].map((step) => (
            <div key={step} className="flex items-center">
              <div
                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-sm sm:text-base ${
                  step <= currentStep
                    ? 'bg-primary text-white'
                    :  'bg-gray-300 text-gray-600'
                }`}
              >
                {step}
              </div>
              {step < 4 && (
                <div
                  className={`w-6 sm:w-24 h-1 ${
                    step < currentStep ? 'bg-primary' : 'bg-gray-300'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-between max-w-3xl mx-auto mt-2 text-xs sm:text-sm px-1">
          <span className={`text-center ${currentStep >= 1 ?  'text-primary font-semibold' : 'text-gray-500'}`}>مراجعة الطلب</span>
          <span className={`text-center ${currentStep >= 2 ? 'text-primary font-semibold' : 'text-gray-500'}`}>بيانات العميل</span>
          <span className={`text-center ${currentStep >= 3 ? 'text-primary font-semibold' : 'text-gray-500'}`}>واتساب</span>
          <span className={`text-center ${currentStep >= 4 ? 'text-primary font-semibold' : 'text-gray-500'}`}>تأكيد نهائي</span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto">
        {/* Step 1: Review Order & Delivery Cost */}
        {currentStep === 1 && (
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">مراجعة طلبك</h2>
            
            <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
              {cartItems.map(item => (
                <div key={item.id} className="flex flex-col sm:flex-row gap-3 sm:gap-4 border-b pb-3 sm:pb-4">
                  <img
                    src={getImageUrl(item.image_url)}
                    alt={item.name}
                    className="w-full sm:w-20 h-32 sm:h-20 object-cover rounded"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-sm sm:text-base">{item.name}</h3>
                    {item.part_number && (
                      <p className="text-xs sm: text-sm text-gray-500">رقم القطعة: {item.part_number}</p>
                    )}
                    <div className="flex items-center justify-between sm:justify-start gap-2 mt-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs sm:text-sm text-gray-600">الكمية:</span>
                        <div className="flex items-center border border-gray-300 rounded">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="px-2 sm:px-3 py-1 hover:bg-gray-100 active:bg-gray-200 text-lg"
                          >
                            -
                          </button>
                          <span className="px-3 sm:px-4 py-1 border-x border-gray-300 min-w-[40px] text-center">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="px-2 sm:px-3 py-1 hover:bg-gray-100 active:bg-gray-200 text-lg"
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <div className="text-right sm:hidden">
                        <p className="font-bold text-sm">{(parseFloat(item.price) || 0).toLocaleString()} د.ع</p>
                        <p className="text-xs text-gray-500">الإجمالي: {((parseFloat(item.price) || 0) * item.quantity).toLocaleString()} د.ع</p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="font-bold">{(parseFloat(item.price) || 0).toLocaleString()} د.ع</p>
                    <p className="text-sm text-gray-500">الإجمالي: {((parseFloat(item.price) || 0) * item.quantity).toLocaleString()} د.ع</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t pt-3 sm: pt-4 space-y-2">
              <div className="flex justify-between text-sm sm:text-base text-gray-600">
                <span>المجموع الفرعي:</span>
                <span className="font-semibold">{subtotal.toLocaleString()} د.ع</span>
              </div>
              <div className="flex justify-between text-sm sm: text-base text-gray-600">
                <span>تكلفة التوصيل:</span>
                <span className="font-semibold">{deliveryCost.toLocaleString()} د.ع</span>
              </div>
              <div className="flex justify-between text-lg sm:text-xl font-bold border-t pt-2">
                <span>المجموع الكلي:</span>
                <span className="text-primary">{total.toLocaleString()} د.ع</span>
              </div>
            </div>

            <button
              onClick={handleNext}
              className="w-full bg-primary text-white py-3 sm: py-4 rounded-lg hover:bg-blue-700 active:bg-blue-800 mt-4 sm:mt-6 text-base sm:text-lg font-semibold"
            >
              متابعة إلى بيانات العميل
            </button>
          </div>
        )}

        {/* Step 2: Customer Details */}
        {currentStep === 2 && (
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-xl sm: text-2xl font-bold mb-4 sm:mb-6">بيانات العميل</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  الاسم الكامل <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={customerDetails.name}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="أدخل اسمك الكامل"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  رقم الهاتف <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={customerDetails.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="07XX XXX XXXX"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  المدينة <span className="text-red-500">*</span>
                </label>
                <select
                  name="city"
                  value={customerDetails.city}
                  onChange={handleInputChange}
                  className="w-full px-3 sm: px-4 py-2 sm:py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus: border-transparent"
                  required
                >
                  <option value="">اختر المدينة</option>
                  {IRAQI_CITIES.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  العنوان <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="address"
                  value={customerDetails.address}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 sm: px-4 py-2 sm:py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus: border-transparent"
                  placeholder="أدخل عنوانك الكامل"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ملاحظات (اختياري)
                </label>
                <textarea
                  name="notes"
                  value={customerDetails.notes}
                  onChange={handleInputChange}
                  rows="2"
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="أي تعليمات أو ملاحظات خاصة"
                />
              </div>
            </div>

            <div className="flex gap-3 sm:gap-4 mt-4 sm:mt-6">
              <button
                onClick={handleBack}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300"
              >
                رجوع
              </button>
              <button
                onClick={handleNext}
                className="flex-1 bg-primary text-white py-3 rounded-lg hover: bg-blue-700"
              >
                متابعة
              </button>
            </div>
          </div>
        )}

        {/* Step 3: WhatsApp Confirmation */}
        {currentStep === 3 && (
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">تأكيد واتساب</h2>
            
            <div className="text-center py-6 sm:py-8">
              <div className="mb-4 sm:mb-6">
                <svg className="w-20 h-20 sm:w-24 sm:h-24 mx-auto text-green-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-. 297-.149-1.758-. 867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-. 883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 . 16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L. 057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
              </div>
              
              <p className="text-gray-600 mb-4">
                سيتم تطبيق خطوة تأكيد واتساب هنا للمستخدمين غير المسجلين. 
              </p>
              
              <p className="text-sm text-gray-500">
                هذه الميزة قيد التطوير حالياً. 
              </p>
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={handleBack}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover: bg-gray-300"
              >
                رجوع
              </button>
              <button
                onClick={handleNext}
                className="flex-1 bg-primary text-white py-3 rounded-lg hover:bg-blue-700"
              >
                متابعة
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Final Confirmation */}
        {currentStep === 4 && (
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">تأكيد الطلب</h2>
            
            <div className="mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold mb-3">معلومات العميل</h3>
              <div className="bg-gray-50 p-3 sm:p-4 rounded space-y-2 text-sm sm:text-base">
                <p><span className="font-medium">الاسم: </span> {customerDetails.name}</p>
                <p><span className="font-medium">الهاتف:</span> {customerDetails.phone}</p>
                <p><span className="font-medium">المدينة: </span> {customerDetails.city}</p>
                <p><span className="font-medium">العنوان:</span> {customerDetails.address}</p>
                {customerDetails.notes && (
                  <p><span className="font-medium">ملاحظات:</span> {customerDetails.notes}</p>
                )}
              </div>
            </div>

            <div className="mb-4 sm:mb-6">
              <h3 className="text-base sm:text-lg font-semibold mb-3">ملخص الطلب</h3>
              <div className="space-y-2 text-sm sm:text-base">
                {cartItems.map(item => (
                  <div key={item.id} className="flex justify-between">
                    <span className="flex-1 pr-2">{item.name} × {item.quantity}</span>
                    <span className="font-semibold whitespace-nowrap">{((parseFloat(item.price) || 0) * item.quantity).toLocaleString()} د.ع</span>
                  </div>
                ))}
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between text-gray-600">
                    <span>المجموع الفرعي:</span>
                    <span className="font-semibold">{subtotal.toLocaleString()} د.ع</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>التوصيل:</span>
                    <span className="font-semibold">{deliveryCost.toLocaleString()} د.ع</span>
                  </div>
                  <div className="flex justify-between font-bold text-base sm:text-lg mt-2">
                    <span>المجموع الكلي:</span>
                    <span className="text-primary">{total.toLocaleString()} د.ع</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
              <p className="text-sm text-yellow-800">
                <strong>ملاحظة:</strong> بتقديم هذا الطلب، فإنك توافق على الشروط والأحكام الخاصة بنا.
                سوف تتلقى مكالمة تأكيد قريباً. 
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={handleBack}
                className="sm:flex-1 bg-gray-200 text-gray-700 py-3 sm:py-3 rounded-lg hover:bg-gray-300 active:bg-gray-400 font-semibold text-base order-2 sm:order-1"
              >
                رجوع
              </button>
              <button
                onClick={handlePlaceOrder}
                className="sm:flex-1 bg-green-600 text-white py-3 sm:py-3 rounded-lg hover:bg-green-700 active:bg-green-800 font-semibold text-base order-1 sm:order-2"
              >
                تأكيد الطلب
              </button>
            </div>
          </div>
        )}

        {/* Step 5: Order Confirmation */}
        {currentStep === 5 && orderConfirmation && (
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            {/* Success Icon */}
            <div className="text-center mb-6">
              <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-10 h-10 sm:w-12 sm:h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-green-600 mb-2">تم تأكيد طلبك!</h2>
              <p className="text-gray-600">شكراً لك على طلبك. سنتواصل معك قريباً.</p>
            </div>

            {/* Order Number */}
            <div className="bg-primary bg-opacity-10 border-2 border-primary rounded-lg p-4 mb-6 text-center">
              <p className="text-sm text-gray-600 mb-1">رقم الطلب</p>
              <p className="text-2xl sm:text-3xl font-bold text-primary">{orderConfirmation.orderNumber}</p>
              <time
                className="block text-xs text-gray-500 mt-2"
                dateTime={orderConfirmation.createdAtISO}
              >
                {orderConfirmation.createdAt}
              </time>
            </div>

            {/* Order Summary */}
            <div className="mb-6">
              <h3 className="text-lg font-bold mb-3 pb-2 border-b">تفاصيل الطلب</h3>
              
              {/* Items */}
              <div className="space-y-3 mb-4">
                {orderConfirmation.items.map(item => (
                  <div key={item.id} className="flex justify-between items-start text-sm sm:text-base">
                    <div className="flex-1">
                      <p className="font-semibold">{item.name}</p>
                      <p className="text-gray-500 text-xs sm:text-sm">الكمية: {item.quantity} × {(parseFloat(item.price) || 0).toLocaleString()} د.ع</p>
                    </div>
                    <p className="font-bold text-primary">{((parseFloat(item.price) || 0) * item.quantity).toLocaleString()} د.ع</p>
                  </div>
                ))}
              </div>

              {/* Price Summary */}
              <div className="border-t pt-3 space-y-2">
                <div className="flex justify-between text-sm sm:text-base text-gray-600">
                  <span>المجموع الفرعي:</span>
                  <span className="font-semibold">{orderConfirmation.subtotal.toLocaleString()} د.ع</span>
                </div>
                <div className="flex justify-between text-sm sm: text-base text-gray-600">
                  <span>تكلفة التوصيل:</span>
                  <span className="font-semibold">{orderConfirmation.deliveryCost.toLocaleString()} د.ع</span>
                </div>
                <div className="flex justify-between text-lg sm:text-xl font-bold pt-2 border-t">
                  <span>المجموع الكلي:</span>
                  <span className="text-green-600">{orderConfirmation.total.toLocaleString()} د.ع</span>
                </div>
              </div>
            </div>

            {/* Delivery Information */}
            <div className="mb-6">
              <h3 className="text-lg font-bold mb-3 pb-2 border-b">معلومات التوصيل</h3>
              <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm sm:text-base">
                <div className="flex">
                  <span className="font-medium w-24">الاسم:</span>
                  <span className="text-gray-700">{orderConfirmation.customerDetails.name}</span>
                </div>
                <div className="flex">
                  <span className="font-medium w-24">الهاتف:</span>
                  <span className="text-gray-700 dir-ltr text-right">{orderConfirmation.customerDetails.phone}</span>
                </div>
                <div className="flex">
                  <span className="font-medium w-24">المدينة:</span>
                  <span className="text-gray-700">{orderConfirmation.customerDetails.city}</span>
                </div>
                <div className="flex">
                  <span className="font-medium w-24">العنوان:</span>
                  <span className="text-gray-700">{orderConfirmation.customerDetails.address}</span>
                </div>
                {orderConfirmation.customerDetails.notes && (
                  <div className="flex">
                    <span className="font-medium w-24">ملاحظات:</span>
                    <span className="text-gray-700">{orderConfirmation.customerDetails.notes}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <div className="flex-1">
                  <p className="font-semibold text-blue-900">طريقة الدفع:  الدفع عند الاستلام</p>
                  <p className="text-sm text-blue-700 mt-1">يمكنك الدفع نقداً عند استلام الطلب من مندوب التوصيل. </p>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h4 className="font-semibold text-green-900 mb-2">الخطوات التالية: </h4>
              <ul className="text-sm text-green-800 space-y-1">
                <li className="flex items-start gap-2">
                  <span className="text-green-600">•</span>
                  <span>سيتم تجهيز طلبك وشحنه في أقرب وقت</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600">•</span>
                  <span>سنرسل لك تحديثات عبر الهاتف</span>
                </li>
              </ul>
            </div>

            {/* Screenshot Instruction */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-. 89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <div className="flex-1">
                  <p className="font-semibold text-blue-900 mb-1">💡 نصيحة:  احفظ هذه الصفحة</p>
                  <p className="text-sm text-blue-700">يمكنك أخذ لقطة شاشة (Screenshot) لهذه الصفحة للاحتفاظ بتفاصيل طلبك كمرجع.</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => navigate('/')}
                className="flex-1 bg-primary text-white py-3 rounded-lg hover:bg-blue-700 active:bg-blue-800 font-semibold"
              >
                العودة للصفحة الرئيسية
              </button>
              {isAuthenticated && (
                <button
                  onClick={() => navigate('/orders')}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 active:bg-gray-400 font-semibold"
                >
                  عرض طلباتي
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutPage;